import { useEffect, useState } from 'react';
import type { FeatureRequest } from '@awwyeah/shared';
import { fetchFeatureRequests } from './api';
import { useFeedbackStatus } from './useFeedbackStatus';

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The recipient's side of the feedback box: a plain, read-only list of ideas the household
 * has sent, newest first. Renders nothing for everyone else. Deliberately no unread
 * counts or badges — Dan reads it when he feels like it (no-demand-mechanics).
 */
export function FeatureRequestsSection(): JSX.Element | null {
  const status = useFeedbackStatus();
  const isRecipient = status?.isRecipient ?? false;
  const [requests, setRequests] = useState<FeatureRequest[] | 'loading' | 'error'>('loading');

  useEffect(() => {
    if (!isRecipient) return undefined;
    let cancelled = false;
    fetchFeatureRequests()
      .then((list) => {
        if (!cancelled) setRequests(list);
      })
      .catch(() => {
        if (!cancelled) setRequests('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isRecipient]);

  useEffect(() => {
    // The push deep-links to `/settings#feature-requests`; scroll once the list exists.
    if (isRecipient && Array.isArray(requests) && window.location.hash === '#feature-requests') {
      document.getElementById('feature-requests')?.scrollIntoView({ block: 'start' });
    }
  }, [isRecipient, requests]);

  if (!isRecipient) return null;

  return (
    <section
      id="feature-requests"
      className="scroll-mt-4 space-y-3 rounded-lg border border-line p-4"
      data-testid="settings-feature-requests"
    >
      <h2 className="text-lg font-semibold text-fg">Ideas from the household</h2>
      {requests === 'loading' && <p className="text-sm text-fg-3">Loading…</p>}
      {requests === 'error' && (
        <p className="text-sm text-fg-3">Couldn&apos;t load these right now.</p>
      )}
      {Array.isArray(requests) && requests.length === 0 && (
        <p className="text-sm text-fg-3">
          Nothing yet. When someone taps &ldquo;Ideas for AwwYeah?&rdquo; it lands here.
        </p>
      )}
      {Array.isArray(requests) && requests.length > 0 && (
        <ul className="divide-y divide-line">
          {requests.map((request) => (
            <li key={request.id} className="space-y-1 py-3 first:pt-0 last:pb-0">
              <p className="text-xs text-fg-3">
                <span className="font-semibold text-fg-2">{request.fromName}</span> ·{' '}
                {formatWhen(request.createdAt)}
                {request.screen && (
                  <>
                    {' '}
                    · on <code className="text-[11px]">{request.screen}</code>
                  </>
                )}
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-fg">{request.text}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
